import { z } from "zod";

import type { PartyKind } from "./directory";

export * from "./agent-runs";
export * from "./api-keys";
export * from "./app-config";
export * from "./bill-payments";
export * from "./countries";
export * from "./customers";
export * from "./directory";
export * from "./directory-search";
export * from "./document-filing";
export * from "./document-links";
export * from "./document-types";
export * from "./exchange-rates";
export * from "./intake";
export * from "./intake-folders";
export * from "./integration";
export * from "./limits";
export * from "./notifications";
export * from "./organizations";
export * from "./other-parties";
export * from "./parties";
export * from "./payees";
export * from "./payment-terms";
export * from "./payments";
export * from "./people";
export * from "./person-contracts";
export * from "./reconciliation";
export * from "./revolut";
export * from "./vendors";
export * from "./work";

/** Paths as seen by the browser; CloudFront forwards them unchanged to the HTTP API. */
export const apiPaths = {
	health: "/api/health",
	/** GET serves the public keys Revolut checks Ravnar's signatures with (`revolutJwksSchema`); no session. */
	revolutJwks: "/api/revolut/jwks",
	/**
	 * GET says whether the organization is connected to Revolut (`revolutConnectionSchema`), POST starts connecting and
	 * answers where to send the browser (`revolutAuthorizationSchema`), DELETE disconnects; owners and admins only.
	 */
	revolutConnection: "/api/revolut/connection",
	/** POST finishes connecting with what Revolut sent the browser back with (`completeRevolutConnectionRequestSchema`). */
	revolutConnectionAuthorization: "/api/revolut/connection/authorization",
	/**
	 * GET says whether Finance may send bills to Revolut as draft payments (`revolutPaymentsSchema`), POST starts
	 * allowing it and answers where to send the browser (`revolutAuthorizationSchema`), DELETE stops it; owners and
	 * admins only.
	 */
	revolutPayments: "/api/revolut/payments",
	/**
	 * POST finishes allowing payments with what Revolut sent the browser back with
	 * (`completeRevolutConnectionRequestSchema`), and answers whether they are allowed (`revolutPaymentsSchema`).
	 */
	revolutPaymentsAuthorization: "/api/revolut/payments/authorization",
	/**
	 * POST sends a vendor's bill to Revolut as a draft payment (`billPaymentRequestSchema`) and answers where it stands
	 * (`billPaymentAnswerSchema`); an editor of Finance.
	 */
	billPayments: "/api/revolut/bill-payments",
	/**
	 * GET answers whether a vendor's bill can be paid with Revolut, to whom, and its draft payment, asking Revolut
	 * where the draft stands (`billPaymentAnswerSchema`) — anyone who can read Finance; DELETE deletes a draft that
	 * still waits for approval, or with `forget=true` forgets one Ravnar cannot follow (`billPaymentDeleteQuerySchema`)
	 * — an editor of Finance. `billPaymentPath` fills in the bill's file id.
	 */
	billPayment: "/api/revolut/bill-payments/{fileId}",
	/** GET lists the connected Revolut accounts with their balances (`revolutAccountsSchema`); anyone who can read Finance. */
	revolutAccounts: "/api/revolut/accounts",
	/**
	 * GET lists one connected account's latest transactions (`revolutTransactionsSchema`); anyone who can read Finance.
	 * `revolutAccountTransactionsPath` fills in the id.
	 */
	revolutAccountTransactions: "/api/revolut/accounts/{accountId}/transactions",
	/**
	 * POST tops up one of an organization's Revolut accounts in Revolut's sandbox (`revolutSandboxTopUpRequestSchema`,
	 * `revolutSandboxTopUpSchema`): a development tool, open — no session, no key — and served on the dev stage alone.
	 */
	revolutSandboxTopUps: "/api/revolut/sandbox/top-ups",
	/**
	 * GET answers the current year's ledger in figures, and where reading it from the bank stands
	 * (`reconciliationSchema`); anyone who can read Finance.
	 */
	reconciliation: "/api/reconciliation",
	/**
	 * GET lists a page of the current year's ledger, the newest first (`ledgerQuerySchema`, `ledgerPageSchema`); anyone
	 * who can read Finance.
	 */
	reconciliationTransactions: "/api/reconciliation/transactions",
	/** POST asks the bank for its latest transactions, for the person asking (`ledgerSyncRequestedSchema`). */
	reconciliationSync: "/api/reconciliation/sync",
	/**
	 * GET answers one transaction of the current year's ledger and the documents that look like its own
	 * (`ledgerTransactionDetailSchema`); anyone who can read Finance. `reconciliationTransactionPath` fills in the id.
	 */
	reconciliationTransaction: "/api/reconciliation/transactions/{transactionId}",
	/**
	 * PUT matches a transaction to a document, or says it needs none (`ledgerResolutionRequestSchema`); DELETE takes back
	 * what resolved it. An editor of Finance; `reconciliationTransactionResolutionPath` fills in the id.
	 */
	reconciliationTransactionResolution: "/api/reconciliation/transactions/{transactionId}/resolution",
	/**
	 * GET searches the organization's Intake documents for one to match a transaction to (`ledgerDocumentsQuerySchema`,
	 * `ledgerDocumentsSchema`); anyone who can read Finance.
	 */
	reconciliationDocuments: "/api/reconciliation/documents",
	/**
	 * GET lists every transaction one document is matched to (`documentPaymentsSchema`); anyone who can read Finance.
	 * `reconciliationDocumentPaymentsPath` fills in the id.
	 */
	reconciliationDocumentPayments: "/api/reconciliation/documents/{fileId}/payments",
	/**
	 * GET answers both sides of the current year's bills and invoices in figures (`paymentsSummarySchema`): each status
	 * counted, what is still to be paid and what of it is overdue. Anyone who can read Finance.
	 */
	payments: "/api/payments",
	/**
	 * GET lists a page of the current year's bills and reimbursement claims, the organization's to pay
	 * (`paymentsQuerySchema`, `paymentsPageSchema`); anyone who can read Finance.
	 */
	payables: "/api/payments/payables",
	/** GET lists a page of the current year's invoices the organization issued, as `payables` does its bills. */
	receivables: "/api/payments/receivables",
	/**
	 * GET answers one bill or invoice of the current year and the bank transactions matched to it
	 * (`paymentDocumentDetailSchema`); anyone who can read Finance. `paymentDocumentPath` fills in the id.
	 */
	paymentDocument: "/api/payments/documents/{fileId}",
	/**
	 * PUT settles one bill or invoice by hand (`settlementRequestSchema`), DELETE takes that back; both answer the
	 * document (`paymentDocumentAnswerSchema`). An editor of Finance; `paymentDocumentSettlementPath` fills in the id.
	 */
	paymentDocumentSettlement: "/api/payments/documents/{fileId}/settlement",
	/**
	 * GET answers the bank account a vendor's bills are paid to, or that it has none (`payeeAnswerSchema`) — anyone who
	 * can read Finance; PUT saves it (`bankAccountSchema`) and DELETE removes it — an editor of Finance. `payeePath`
	 * fills in the vendor's id.
	 */
	payee: "/api/payments/payees/{vendorId}",
	/**
	 * GET answers the ECB's euro reference rates the API last read (`exchangeRatesSchema`), or that it could not read
	 * them; any member.
	 */
	exchangeRates: "/api/exchange-rates",
	/** POST starts creating an organization: Cognito emails its owner a code that confirms their email. */
	organizations: "/api/organizations",
	/** POST confirms that code, creates the organization and signs its owner in. */
	organizationConfirmation: "/api/organizations/confirmation",
	/** POST signs in, GET returns the current session, DELETE signs out. */
	session: "/api/session",
	/** POST answers the new-password challenge of a first sign-in with a temporary password. */
	sessionNewPassword: "/api/session/new-password",
	/** POST changes the signed-in member's password. */
	sessionPassword: "/api/session/password",
	/** POST asks for a 6-digit code that resets a forgotten password. */
	sessionPasswordReset: "/api/session/password-reset",
	/** POST sets a new password with that code and signs in. */
	sessionPasswordResetConfirm: "/api/session/password-reset/confirm",
	/** POST answers the authenticator-app challenge of a sign-in and opens the session. */
	sessionMfa: "/api/session/mfa",
	/** POST starts setting up an authenticator app during a sign-in and answers with the shared secret. */
	sessionMfaSetup: "/api/session/mfa/setup",
	/** POST confirms the first code and opens the session. */
	sessionMfaVerify: "/api/session/mfa/verify",
	/** GET reads the organization's security rules (`organizationSecurityStatusSchema`), PUT saves them. */
	organizationSecurity: "/api/organization/security",
	/**
	 * GET answers where the organization stands against its limits — its storage and this month's AI tokens
	 * (`organizationLimitsSchema`); any member.
	 */
	organizationLimits: "/api/organization/limits",
	/**
	 * GET lists the organization's API keys (`apiKeysSchema`), POST creates one (`createApiKeyRequestSchema`,
	 * `createdApiKeySchema`); owners and admins only.
	 */
	apiKeys: "/api/api-keys",
	/** DELETE revokes one API key; `apiKeyPath` fills in the id. Owners and admins only. */
	apiKey: "/api/api-keys/{keyId}",
	/**
	 * POST adds a person, a vendor, a customer or an "other" party (`integrationPartyRequestSchema`, `addedPartySchema`):
	 * the integration API, which software calls with an API key (`Authorization: Bearer <key>`) and never a session.
	 */
	integrationParties: "/api/v1/parties",
	/**
	 * POST takes one JSON-RPC message of the Model Context Protocol, and GET is answered 405: the MCP server chatbots and
	 * agents reach with an API key, whose tools add the same parties and search the Directory
	 * (`integrationDirectorySearchSchema`, `integrationDirectoryFoundSchema`).
	 */
	integrationMcp: "/api/v1/mcp",
	/**
	 * POST asks where to upload a document to Intake (`integrationDocumentRequestSchema`,
	 * `integrationDocumentUploadSchema`), and its bytes then go to the URL it answers, as the Intake page's do: the
	 * integration API, with an API key.
	 */
	integrationDocuments: "/api/v1/documents",
	/**
	 * GET says where one of the organization's documents stands in Intake (`integrationDocumentSchema`), with an API
	 * key; `integrationDocumentPath` fills in the id.
	 */
	integrationDocument: "/api/v1/documents/{documentId}",
	/** GET says whether the signed-in member has an authenticator app, DELETE turns it off. */
	profileMfa: "/api/profile/mfa",
	/** POST starts setting one up and answers with the shared secret (`mfaSecretResponseSchema`). */
	profileMfaSetup: "/api/profile/mfa/setup",
	/** POST confirms the first code from the app (`verifyMfaCodeRequestSchema`). */
	profileMfaVerify: "/api/profile/mfa/verify",
	/** GET returns the company identity, PUT saves it. */
	companyIdentity: "/api/organization/identity",
	/** GET lists the team, POST adds a member. */
	teamMembers: "/api/team/members",
	/** PATCH changes a member, DELETE removes one; `teamMemberPath` fills in the id. */
	teamMember: "/api/team/members/{memberId}",
	/** DELETE turns off a member's authenticator app so they can set a new one up; `teamMemberMfaPath` fills in the id. */
	teamMemberMfa: "/api/team/members/{memberId}/mfa",
	/** GET lists the audit log a page at a time, newest first (`auditLogQuerySchema`, `auditLogPageSchema`). */
	auditLog: "/api/audit-log",
	/** POST starts an upload for Intake and answers where to put the file (`createIntakeUploadRequestSchema`). */
	intakeUploads: "/api/intake/uploads",
	/** GET lists a page of the organization's Intake documents, newest first (`intakeFilesQuerySchema`, `intakeFilesPageSchema`). */
	intakeFiles: "/api/intake/files",
	/** GET reads one of the organization's Intake documents (`intakeDocumentSchema`); `intakeFilePath` fills in the id. */
	intakeFile: "/api/intake/files/{fileId}",
	/** GET presigns a download of one stored document (`intakeDownloadResponseSchema`); `intakeFileDownloadPath` fills in the id. */
	intakeFileDownload: "/api/intake/files/{fileId}/download",
	/** GET presigns where to show one stored document from (`intakePreviewResponseSchema`); `intakeFilePreviewPath` fills in the id. */
	intakeFilePreview: "/api/intake/files/{fileId}/preview",
	/**
	 * GET lists the parties one document is linked to (`documentPartiesSchema`), POST links it to one more
	 * (`linkDocumentPartyRequestSchema`); `intakeFilePartiesPath` fills in the id.
	 */
	intakeFileParties: "/api/intake/files/{fileId}/parties",
	/** DELETE unlinks one document from one party; `intakeFilePartyPath` fills in the three. */
	intakeFileParty: "/api/intake/files/{fileId}/parties/{kind}/{partyId}",
	/**
	 * GET answers what Document Filing read from one document, or that it has not read it (`documentFilingAnswerSchema`);
	 * any member. `intakeFileFilingPath` fills in the id.
	 */
	intakeFileFiling: "/api/intake/files/{fileId}/filing",
	/** GET lists the organization's people a page at a time, A→Z by name; POST adds one. */
	people: "/api/people",
	/** GET lists the roles and the teams the organization has given its people (`rolesAndTeamsSchema`). */
	peopleRolesAndTeams: "/api/people/roles-and-teams",
	/** GET reads one person, PATCH changes them, DELETE removes them; `personPath` fills in the id. */
	person: "/api/people/{personId}",
	/** POST adds a contract to one person (`personContractRequestSchema`); `personContractsPath` fills in the id. */
	personContracts: "/api/people/{personId}/contracts",
	/** PATCH changes one of a person's contracts, DELETE removes it; `personContractPath` fills in both ids. */
	personContract: "/api/people/{personId}/contracts/{contractId}",
	/** GET lists a page of the organization's directory, A→Z by name (`directoryQuerySchema`, `directoryPageSchema`). */
	directory: "/api/directory",
	/**
	 * GET the parties the Directory's knowledge base finds most like a search, whose names do not hold it
	 * (`directoryRelatedQuerySchema`, `directoryRelatedSchema`).
	 */
	directoryRelated: "/api/directory/related",
	/**
	 * GET lists the documents one party of the Directory is linked to, newest first (`partyDocumentsSchema`);
	 * `directoryPartyDocumentsPath` fills in the kind and the id.
	 */
	directoryPartyDocuments: "/api/directory/{kind}/{partyId}/documents",
	/** GET lists the organization's customers A→Z, POST adds one. */
	customers: "/api/customers",
	/** GET reads one customer, PATCH changes it, DELETE removes it; `customerPath` fills in the id. */
	customer: "/api/customers/{customerId}",
	/** GET lists the organization's other parties A→Z, POST adds one. */
	otherParties: "/api/other-parties",
	/** GET reads one, PATCH changes it, DELETE removes it; `otherPartyPath` fills in the id. */
	otherParty: "/api/other-parties/{partyId}",
	/** GET lists the organization's vendors A→Z, POST adds one. */
	vendors: "/api/vendors",
	/** GET reads one vendor, PATCH changes it, DELETE removes it; `vendorPath` fills in the id. */
	vendor: "/api/vendors/{vendorId}",
	/** GET lists a page of the member's notifications, newest first (`notificationsQuerySchema`, `notificationsPageSchema`). */
	notifications: "/api/notifications",
	/** PATCH archives or restores one notification (`updateNotificationRequestSchema`); `notificationPath` fills in the id. */
	notification: "/api/notifications/{notificationId}",
	/** GET lists a page of the work items the member may see, in one state (`workItemsQuerySchema`, `workItemsPageSchema`). */
	workItems: "/api/work-items",
	/** GET reads one work item (`workItemSchema`); `workItemPath` fills in the id. */
	workItem: "/api/work-items/{workItemId}",
} as const;

/** The path of one Revolut account's transactions, for GET. */
export function revolutAccountTransactionsPath(accountId: string): string {
	return apiPaths.revolutAccountTransactions.replace("{accountId}", () => encodeURIComponent(accountId));
}

/** The path of one team member, for PATCH and DELETE. */
export function teamMemberPath(memberId: string): string {
	return apiPaths.teamMember.replace("{memberId}", () => encodeURIComponent(memberId));
}

/** The path that resets one team member's authenticator app. */
export function teamMemberMfaPath(memberId: string): string {
	return apiPaths.teamMemberMfa.replace("{memberId}", () => encodeURIComponent(memberId));
}

/** The path of one API key, for DELETE. */
export function apiKeyPath(keyId: string): string {
	return apiPaths.apiKey.replace("{keyId}", () => encodeURIComponent(keyId));
}

/** The path of one document in the integration API, for GET. */
export function integrationDocumentPath(documentId: string): string {
	return apiPaths.integrationDocument.replace("{documentId}", () => encodeURIComponent(documentId));
}

/** The path of one person, for GET, PATCH and DELETE. */
export function personPath(personId: string): string {
	return apiPaths.person.replace("{personId}", () => encodeURIComponent(personId));
}

/** The path that adds a contract to one person, for POST. */
export function personContractsPath(personId: string): string {
	return apiPaths.personContracts.replace("{personId}", () => encodeURIComponent(personId));
}

/** The path of one of a person's contracts, for PATCH and DELETE. */
export function personContractPath(personId: string, contractId: string): string {
	return apiPaths.personContract
		.replace("{personId}", () => encodeURIComponent(personId))
		.replace("{contractId}", () => encodeURIComponent(contractId));
}

/** The path of one customer, for GET, PATCH and DELETE. */
export function customerPath(customerId: string): string {
	return apiPaths.customer.replace("{customerId}", () => encodeURIComponent(customerId));
}

/** The path of one "other" party, for GET, PATCH and DELETE. */
export function otherPartyPath(partyId: string): string {
	return apiPaths.otherParty.replace("{partyId}", () => encodeURIComponent(partyId));
}

/** The path of one vendor, for GET, PATCH and DELETE. */
export function vendorPath(vendorId: string): string {
	return apiPaths.vendor.replace("{vendorId}", () => encodeURIComponent(vendorId));
}

/** The path of one work item, for GET. */
export function workItemPath(workItemId: string): string {
	return apiPaths.workItem.replace("{workItemId}", () => encodeURIComponent(workItemId));
}

/** The path of one notification, for PATCH. */
export function notificationPath(notificationId: string): string {
	return apiPaths.notification.replace("{notificationId}", () => encodeURIComponent(notificationId));
}

/** The path of one Intake document, for GET. */
export function intakeFilePath(fileId: string): string {
	return apiPaths.intakeFile.replace("{fileId}", () => encodeURIComponent(fileId));
}

/** The path of one transaction of the ledger, for GET. */
export function reconciliationTransactionPath(transactionId: string): string {
	return apiPaths.reconciliationTransaction.replace("{transactionId}", () => encodeURIComponent(transactionId));
}

/** The path of what resolves one transaction of the ledger, for PUT and DELETE. */
export function reconciliationTransactionResolutionPath(transactionId: string): string {
	return apiPaths.reconciliationTransactionResolution.replace("{transactionId}", () =>
		encodeURIComponent(transactionId),
	);
}

/** The path of the transactions one document is matched to, for GET. */
export function reconciliationDocumentPaymentsPath(fileId: string): string {
	return apiPaths.reconciliationDocumentPayments.replace("{fileId}", () => encodeURIComponent(fileId));
}

/** The path of one bill or invoice the Payables and Receivables pages follow, for GET. */
export function paymentDocumentPath(fileId: string): string {
	return apiPaths.paymentDocument.replace("{fileId}", () => encodeURIComponent(fileId));
}

/** The path of what settles one bill or invoice by hand, for PUT and DELETE. */
export function paymentDocumentSettlementPath(fileId: string): string {
	return apiPaths.paymentDocumentSettlement.replace("{fileId}", () => encodeURIComponent(fileId));
}

/** The path of one bill's draft payment, for GET and DELETE. */
export function billPaymentPath(fileId: string): string {
	return apiPaths.billPayment.replace("{fileId}", () => encodeURIComponent(fileId));
}

/** The path of the bank account one vendor is paid at, for GET, PUT and DELETE. */
export function payeePath(vendorId: string): string {
	return apiPaths.payee.replace("{vendorId}", () => encodeURIComponent(vendorId));
}

/** The path that presigns one document's download. */
export function intakeFileDownloadPath(fileId: string): string {
	return apiPaths.intakeFileDownload.replace("{fileId}", () => encodeURIComponent(fileId));
}

/** The path that presigns where to show one document from. */
export function intakeFilePreviewPath(fileId: string): string {
	return apiPaths.intakeFilePreview.replace("{fileId}", () => encodeURIComponent(fileId));
}

/** The path of the parties one document is linked to, for GET and POST. */
export function intakeFilePartiesPath(fileId: string): string {
	return apiPaths.intakeFileParties.replace("{fileId}", () => encodeURIComponent(fileId));
}

/** The path of what Document Filing read from one document, for GET. */
export function intakeFileFilingPath(fileId: string): string {
	return apiPaths.intakeFileFiling.replace("{fileId}", () => encodeURIComponent(fileId));
}

/** The path of one document's link to one party, for DELETE. */
export function intakeFilePartyPath(fileId: string, kind: PartyKind, partyId: string): string {
	return apiPaths.intakeFileParty
		.replace("{fileId}", () => encodeURIComponent(fileId))
		.replace("{kind}", () => encodeURIComponent(kind))
		.replace("{partyId}", () => encodeURIComponent(partyId));
}

/** The path of the documents one party is linked to, for GET. */
export function directoryPartyDocumentsPath(kind: PartyKind, partyId: string): string {
	return apiPaths.directoryPartyDocuments
		.replace("{kind}", () => encodeURIComponent(kind))
		.replace("{partyId}", () => encodeURIComponent(partyId));
}

export const healthResponseSchema = z.object({
	status: z.literal("ok"),
	stage: z.string(),
	timestamp: z.iso.datetime(),
});
export type HealthResponse = z.infer<typeof healthResponseSchema>;

export const apiErrorSchema = z.object({
	error: z.string(),
	issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;
